import React, { useEffect, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, useLocation } from "react-router-dom";
// Sections
import TopNavbar from "./components/Nav/TopNavbar";
import Hero from "./components/Sections/Hero";
import About from "./components/Sections/About";
import Services from "./components/Sections/Services";
import Impact from "./components/Sections/Impact";
import Partners from "./components/Sections/Partners";
import Contact from "./components/Sections/Contact";
import Footer from "./components/Sections/Footer";
// Elements
import CookieConsent from "./components/Elements/CookieConsent";
// Utils
import { optimizeForMobile } from "./utils/mobileOptimization";
// Pages - lazy loaded for code splitting
const LaunchEvent = lazy(() => import("./components/Pages/LaunchEvent"));
const WhoIsAntonFor = lazy(() => import("./components/Pages/WhoIsAntonFor"));
const DeveloperToolsPage = lazy(() => import("./components/Pages/DeveloperToolsPage"));
const TermsAndConditions = lazy(() => import("./components/Pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("./components/Pages/PrivacyPolicy"));

function Landing() {
  const location = useLocation();
  
  useEffect(() => {
    // Handle hash navigation from other pages
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      // Scroll to top if no hash is present (direct navigation to landing page)
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  useEffect(() => {
    // Detect mobile device with immediate exit
    const checkMobile = () => {
      const mobile = typeof window !== 'undefined' && (
        window.innerWidth <= 768 || 
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        'ontouchstart' in window
      );
      return mobile;
    };

    const mobile = checkMobile();
    
    // Completely skip ALL animations and effects on mobile
    if (mobile) {
      console.log('Mobile detected - all animations disabled for Landing page');
      return () => {}; // No cleanup needed, no initialization
    }
    
    // Desktop-only animation initialization
    try {
      console.log('Desktop detected - initializing animations');
      
      // Initialize scroll animations with proper hook usage
      const cleanupScrollAnimations = () => {
        // Basic scroll animations without complex hooks
        return () => {};
      };
      
      // Initialize parallax effects
      const cleanupParallax = () => {
        // Basic parallax without complex dependencies
        return () => {};
      };
      
      // Cleanup function
      return () => {
        cleanupScrollAnimations();
        cleanupParallax();
      };
    } catch (error) {
      console.warn('Animation initialization failed:', error);
      return () => {};
    }
  }, []);

  return (
    <>
      <TopNavbar />
      <Hero />
      <About />
      <Services />
      <Impact />
      <Partners />
      <Contact />
      <Footer />
    </>
  );
}

function App() {
  useEffect(() => {
    // Apply mobile optimizations immediately
    optimizeForMobile();
    
    // Add Tally.so script
    const script = document.createElement('script');
    script.src = 'https://tally.so/widgets/embed.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <Router>
      <div className="App">
                  <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/launch-event" element={<LaunchEvent />} />
              <Route path="/who-is-anton-for" element={<WhoIsAntonFor />} />
              <Route path="/developer-tools" element={<DeveloperToolsPage />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            </Routes>
          </Suspense>
        <CookieConsent />
      </div>
    </Router>
  );
}

export default App;
